import { showToast } from '@crowdex/ui/components/toaster';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { setAllAppsOpen } from '@/components/sidebar/all-apps';
import { useShellSearchShortcuts } from '@/components/sidebar/search-dialog';
import type { ShellNavigation } from '@/components/sidebar/shell-navigation';
import { useShellShortcuts } from '@/components/sidebar/shell-shortcuts';
import { goBackInApp } from '@/lib/app-home';
import type { ConversationDockSide } from '@/lib/conversations/conversation-layout';
import { createConversationServerActions } from '@/lib/desktop-shell/conversation-server-actions';
import type { DesktopShellStore } from '@/lib/desktop-shell/store';
import { useLibraryPage, useRouter } from '@/lib/navigation';
import type { SidebarThread } from '@/lib/sidebar-threads';

/**
 * What the sidebar, the drawer, ⌘K and the conversation menus act through (`ShellNavigation`),
 * from the shell's navigation and conversation controls, and the shell's keyboard shortcuts
 * (⌘N, ⌘S, ⌘[ ⌘], ⌘, ⌘/, ⌘K, ⌃1–⌃3). Leaving for a conversation leaves All apps and a library
 * page first.
 */
export function useShellNavigation(input: {
  store: DesktopShellStore;
  preview: boolean;
  /** A preview's new conversation is none of the list. */
  previewConversation: boolean;
  phone: boolean;
  chatId: string;
  activeThreadId: string;
  threads: readonly SidebarThread[];
  busy: boolean;
  cloudAgentSelected: boolean;
  activateSidebarThread: (thread: SidebarThread) => void;
  openConversation: (conversationId: string) => Promise<boolean>;
  resetConversation: () => unknown;
  openThreadInSplit: (
    thread: SidebarThread,
    targetId: string,
    side: ConversationDockSide,
  ) => Promise<void>;
  startThreadPointerDrag: (thread: SidebarThread, event: ReactPointerEvent<HTMLElement>) => void;
  navigateConversation: (direction: -1 | 1) => Promise<void>;
  stopAgentResponse: () => void;
  continueLocally: () => Promise<unknown>;
  toggleSidebarHidden: () => void;
  /** Opens the add-repository dialog (a folder, a clone). */
  addRepository: () => void;
}): ShellNavigation {
  const {
    store,
    preview,
    chatId,
    threads,
    activateSidebarThread,
    resetConversation,
    navigateConversation,
  } = input;
  const router = useRouter();
  const library = useLibraryPage();
  const leaveOverlays = () => {
    setAllAppsOpen(false);
    library.leave();
  };
  useShellShortcuts(
    {
      newTask: () => {
        leaveOverlays();
        resetConversation();
      },
      toggleSidebar: input.toggleSidebarHidden,
      navigate: (direction) => {
        // An app full screen goes up its own levels first (an item, a route), as its chevron.
        if (direction === -1 && goBackInApp()) return;
        leaveOverlays();
        void navigateConversation(direction);
      },
      openSettings: () => router.push('/settings'),
      openShortcuts: () => router.push('/settings?section=shortcuts'),
    },
    !input.phone,
  );
  useShellSearchShortcuts();
  const serverActions = createConversationServerActions({
    store,
    preview,
    resetConversation: () => void resetConversation(),
  });
  return {
    preview,
    threads,
    activeThreadId: preview && !input.previewConversation ? chatId : input.activeThreadId,
    openThread: (thread) => {
      leaveOverlays();
      activateSidebarThread(thread);
    },
    openConversation: (conversationId) => {
      leaveOverlays();
      const thread = threads.find((entry) => entry.id === conversationId);
      if (thread !== undefined) activateSidebarThread(thread);
      else if (!preview)
        void input.openConversation(conversationId).then((shown) => {
          if (!shown)
            showToast({
              title: 'This conversation could not be opened.',
              description: 'It may have been archived or deleted. Find it with ⌘K.',
              tone: 'error',
            });
        });
    },
    newTask: () => {
      leaveOverlays();
      resetConversation();
    },
    openSideBySide: (thread) => {
      leaveOverlays();
      void input.openThreadInSplit(thread, chatId, 'right');
    },
    startDrag: input.startThreadPointerDrag,
    stopAgent: (thread) => {
      if (thread.id === chatId && input.busy) input.stopAgentResponse();
    },
    ...(input.cloudAgentSelected ? { continueLocally: () => void input.continueLocally() } : {}),
    ...serverActions,
    addRepository: input.addRepository,
  };
}
